import { isSupportViewActive } from "~/lib/impersonation";
import { onProofRoute, op } from "~/lib/openpanel";

// Multi-branch signup, plus the standalone conversion flows a customer can walk
// through after onboarding. Kept in sync with the flow layouts that call
// `useFlowStart` and the mutation handlers that emit `_completed` / `_failed`.
export type FlowName =
  | "onboarding"
  | "machtiging"
  | "uitbetaling"
  | "energiefactuur_upload"
  | "laadpaal_fotos_upload"
  | "verbind_account"
  | "vriendenvoordeel";

// Only the multi-step flows emit `_step_completed`; the rest are single-shot.
type StepFlowName = "onboarding" | "machtiging";

type FlowEvent =
  | `${FlowName}_started`
  | `${FlowName}_completed`
  | `${FlowName}_failed`
  | `${StepFlowName}_step_completed`;

type LoginEvent =
  "login_started" | "login_otp_submitted" | "login_completed" | "login_failed";

// An overstapper lands via utm_campaign=overstap, then is linked to their account.
// utm_source and the rest ride along as global properties (see `captureAttribution`).
type OverstapperEvent = "overstapper_captured" | "overstapper_claimed";

/** The closed set of manual events. `track()` rejects anything outside it. */
export type TrackEvent = FlowEvent | LoginEvent | OverstapperEvent;

/**
 * Whether analytics should reach OpenPanel at all: never in dev/test, and never
 * without a configured client id (the same conditions under which the SDK's own
 * automatic tracking is inert). Exported so `useIdentify` and the attribution
 * helper apply exactly the same gate.
 */
export function analyticsEnabled(): boolean {
  return (
    !import.meta.env.DEV &&
    import.meta.env.MODE !== "test" &&
    Boolean(import.meta.env.VITE_OPENPANEL_CLIENT_ID)
  );
}

export function shouldTrack(): boolean {
  return analyticsEnabled() && !onProofRoute() && !isSupportViewActive();
}

/**
 * Typed wrapper around `op.track`. `event` is constrained to the known
 * taxonomy; `props` is free-form. No-op in dev/test, without a client id, and
 * on the proof route. Fire-and-forget — the returned promise is swallowed so a
 * failed beacon never surfaces to the user.
 */
export function track(event: TrackEvent, props?: Record<string, unknown>): void {
  if (!shouldTrack()) return;
  void op.track(event, props).catch(() => {
    // Analytics beacons are best-effort; a network failure is not worth logging.
  });
}

/**
 * The account-connect screens are shared between onboarding and the
 * post-onboarding reconnect flow, so a connect failure is reported into
 * whichever funnel the user is in.
 */
export function trackConnectFailure(
  isReconnect: boolean,
  onboardingStep: string,
  props?: Record<string, unknown>,
): void {
  if (isReconnect) track("verbind_account_failed", props);
  else track("onboarding_failed", { step: onboardingStep, ...props });
}
